// OpsEventos — admin event-floor view (Sophi App v2 Piece 9). Renders only
// when AuthUser.isAdmin. Pulls /events/today-all and shows per-event closure
// progress so Karen/Angie/Issamer/Wendy can scan the whole night without opening the
// dashboard. Polling cadence is conservative (15s) since this screen is
// awareness-oriented, not action-driven.
//
// Tap an event → opens EventChat (admin spy mode) or CerrarEvento (force-close).
// Below today's floor, a lightweight "Próximos" list (next 14 days) lets Ops
// open a future event's chat to coordinate ahead of time (pre-event the chat
// is read-only for staff; captain + Ops can post).

import { useEffect, useState } from 'preact/hooks';
import {
  getOpsEventsToday,
  type OpsEventSummary,
  type OpsUpcomingEvent,
  type RosterMember,
} from '../lib/api';
import { EventChat } from './EventChat';
import { CerrarEvento } from './CerrarEvento';
import { ResultadosEvento } from './ResultadosEvento';

const POLL_INTERVAL_MS = 15000;

export function OpsEventos() {
  const [events, setEvents] = useState<OpsEventSummary[] | null>(null);
  const [upcoming, setUpcoming] = useState<OpsUpcomingEvent[]>([]);
  const [cerradosHoy, setCerradosHoy] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [chatEvent, setChatEvent] = useState<{ id: string; title: string } | null>(null);
  const [cerrarEvent, setCerrarEvent] = useState<{ id: string; title: string } | null>(null);
  const [resultadosEvent, setResultadosEvent] = useState<{ id: string; title: string } | null>(null);
  const [openRoster, setOpenRoster] = useState<Record<string, boolean>>({});
  const [openReportes, setOpenReportes] = useState<Record<string, boolean>>({});

  async function load() {
    try {
      const r = await getOpsEventsToday();
      setEvents(r.events);
      setUpcoming(r.upcoming || []);
      setCerradosHoy(r.cerradosHoy || 0);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    async function tick() {
      if (document.hidden) { t = setTimeout(tick, POLL_INTERVAL_MS); return; }
      if (!cancelled) await load();
      if (!cancelled) t = setTimeout(tick, POLL_INTERVAL_MS);
    }
    t = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; if (t) clearTimeout(t); };
  }, []);

  if (chatEvent) {
    return (
      <EventChat
        eventId={chatEvent.id}
        eventTitle={chatEvent.title}
        onClose={() => setChatEvent(null)}
      />
    );
  }
  if (cerrarEvent) {
    return (
      <CerrarEvento
        eventId={cerrarEvent.id}
        eventTitle={cerrarEvent.title}
        onClose={() => { setCerrarEvent(null); load(); }}
        onShowResults={() => { const ev = cerrarEvent; setCerrarEvent(null); setResultadosEvent(ev); }}
      />
    );
  }
  if (resultadosEvent) {
    return (
      <ResultadosEvento
        eventId={resultadosEvent.id}
        eventTitle={resultadosEvent.title}
        onClose={() => { setResultadosEvent(null); load(); }}
      />
    );
  }

  if (events === null) {
    return (
      <div class="screen">
        <header class="app-header"><h1>Ops · Eventos sin cerrar</h1></header>
        {error
          ? <div class="error-screen"><p>{error}</p><button class="btn primary" onClick={load}>Reintentar</button></div>
          : <div class="loading"><span class="spinner" /></div>}
      </div>
    );
  }

  return (
    <div class="screen">
      <header class="app-header">
        <h1>Ops · Eventos sin cerrar</h1>
        <div class="subtitle">
          {events.length} sin cerrar
          {cerradosHoy > 0 && ` · ${cerradosHoy} cerrados hoy`}
          {upcoming.length > 0 && ` · ${upcoming.length} próximos`}
        </div>
      </header>

      {events.length === 0 && (
        <div class="empty">
          <div class="ic">🎉</div>
          <p>No hay eventos sin cerrar.</p>
          {cerradosHoy > 0 && <p class="hint">{cerradosHoy} cerrados hoy.</p>}
        </div>
      )}

      {events.map((e) => {
        const stateLabel = closureLabel(e.closureState);
        const stateClass = closureClass(e.closureState);
        const reportados = e.roster.filter((s) => s.reported).length;
        return (
          <div key={e.id} class={`ops-event-card ${stateClass}`}>
            <div class="ops-event-head">
              <div class="ops-event-title">{e.title}</div>
              <span class={`ops-event-badge ${stateClass}`}>{stateLabel}</span>
            </div>
            {(e.daysOverdue ?? 0) > 0 && (
              <div class="ops-overdue">
                ⚠️ Sin cerrar · {formatUpcomingDate(e.date)} (hace {e.daysOverdue} {e.daysOverdue === 1 ? 'día' : 'días'})
              </div>
            )}
            <div class="ops-event-meta">
              {e.startTime && <span>🕒 {e.startTime}</span>}
              {e.location && <span>📍 {e.location}</span>}
              {e.capitanNombre && <span>👑 {e.capitanNombre}</span>}
            </div>
            <button
              type="button"
              class="ops-progress-toggle"
              onClick={() => setOpenReportes((o) => ({ ...o, [e.id]: !o[e.id] }))}
            >
              <div class="ops-event-progress">
                <div class="ops-event-progress-bar">
                  <div
                    class="ops-event-progress-fill"
                    style={{ width: e.teamSize > 0 ? `${(reportados / e.teamSize) * 100}%` : '0%' }}
                  />
                </div>
                <div class="ops-event-progress-count">
                  {reportados} / {e.teamSize} reportes
                  <span class="ops-arrival-caret">{openReportes[e.id] ? ' ▲' : ' ▼'}</span>
                </div>
              </div>
            </button>
            {openReportes[e.id] && (
              <div class="ops-reporters">
                {e.roster.filter((s) => s.reported).length > 0
                  ? e.roster.filter((s) => s.reported).map((s) => (
                      <div key={s.staffId || s.nombre} class="ops-reporter-row">
                        <span class="ops-reporter-tick">✅</span> {s.nombre}
                        {s.rol && <span class="ops-roster-rol"> · {s.rol}</span>}
                      </div>
                    ))
                  : <div class="ops-reporter-empty">Nadie ha reportado todavía.</div>}
              </div>
            )}
            {e.arrivalSummary && e.arrivalSummary.total > 0 && (
              <div class="ops-arrival">
                <button
                  type="button"
                  class="ops-arrival-toggle"
                  onClick={() => setOpenRoster((o) => ({ ...o, [e.id]: !o[e.id] }))}
                >
                  <span class={`ops-arrival-summary${e.arrivalSummary.noLlegaron > 0 ? ' alert' : ''}`}>
                    🚶 {arrivalSummaryLine(e.arrivalSummary)}
                  </span>
                  <span class="ops-arrival-caret">{openRoster[e.id] ? '▲' : '▼'}</span>
                </button>
                {openRoster[e.id] && (
                  <div class="ops-roster">
                    {e.roster.map((s) => (
                      <div key={s.staffId || s.nombre} class="ops-roster-row">
                        <span class="ops-roster-name">
                          {s.nombre}
                          {s.rol && <span class="ops-roster-rol"> · {s.rol}</span>}
                        </span>
                        <div class="ops-roster-chips">
                          <span class={`ops-arrival-chip ${arrivalClass(s.arrivalStatus)}`}>
                            {arrivalLabel(s)}
                            {s.arrivalStatus === 'no_ha_llegado' && s.telegram && (
                              <ContactLink tg={s.telegram} />
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div class="ops-event-actions">
              <button class="btn ghost" onClick={() => setChatEvent({ id: e.id, title: e.title })}>
                💬 Ver chat
              </button>
              <button class="btn primary" onClick={() => setCerrarEvent({ id: e.id, title: e.title })}>
                🏁 Cierre
              </button>
            </div>
          </div>
        );
      })}

      {upcoming.length > 0 && (
        <>
          <div class="section-header">Próximos (14 días)</div>
          {upcoming.map((e) => (
            <div key={e.id} class="ops-event-card">
              <div class="ops-event-head">
                <div class="ops-event-title">{e.title}</div>
              </div>
              <div class="ops-event-meta">
                <span>📅 {formatUpcomingDate(e.date)}</span>
                {e.startTime && <span>🕒 {e.startTime}</span>}
                {e.location && <span>📍 {e.location}</span>}
              </div>
              <div class="ops-event-meta">
                {e.capitanNombre && <span>👑 {e.capitanNombre}</span>}
                <span>👥 {e.teamSize} en el equipo</span>
              </div>
              <div class="ops-event-actions">
                <button class="btn ghost" onClick={() => setChatEvent({ id: e.id, title: e.title })}>
                  💬 Ver chat
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// "2026-06-20" → "vie 20 jun". Noon-AST anchor avoids the off-by-one a bare
// Date('YYYY-MM-DD') (UTC midnight) causes on UTC-4 devices.
const DAY_ES_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MONTH_ES_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function formatUpcomingDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const d = new Date(`${iso}T12:00:00-04:00`);
  if (isNaN(d.getTime())) return iso;
  return `${DAY_ES_SHORT[d.getDay()]} ${parseInt(m[3], 10)} ${MONTH_ES_SHORT[d.getMonth()]}`;
}

function closureLabel(s: OpsEventSummary['closureState']): string {
  switch (s) {
    case 'not_initiated': return 'Sin iniciar';
    case 'collecting': return 'Recolectando';
    case 'finalizing': return 'Cerrando';
  }
}

function closureClass(s: OpsEventSummary['closureState']): string {
  switch (s) {
    case 'not_initiated': return 'ops-state-pending';
    case 'collecting': return 'ops-state-collecting';
    case 'finalizing': return 'ops-state-finalizing';
  }
}

function arrivalSummaryLine(as: OpsEventSummary['arrivalSummary']): string {
  const bits = [`${as.llegaron}/${as.total} llegaron`];
  if (as.tarde) bits.push(`${as.tarde} tarde`);
  if (as.porLlegar) bits.push(`${as.porLlegar} por llegar`);
  if (as.noLlegaron) bits.push(`${as.noLlegaron} sin llegar`);
  return bits.join(' · ');
}

function arrivalLabel(s: RosterMember): string {
  switch (s.arrivalStatus) {
    case 'llego_tarde': return `Tarde ${s.checkInLocal}${s.deltaMin != null ? ` (+${s.deltaMin} min)` : ''}`;
    case 'por_llegar': return 'Por llegar';
    case 'no_ha_llegado': return `No ha llegado${s.deltaMin != null ? ` (+${s.deltaMin} min)` : ''}`;
    case 'llego_a_tiempo': return `Llegó ${s.checkInLocal}`;
    default: return s.checkInLocal ? `Llegó ${s.checkInLocal}` : '—';
  }
}

function arrivalClass(st: RosterMember['arrivalStatus']): string {
  switch (st) {
    case 'llego_a_tiempo': return 'ops-arrival-ok';
    case 'llego_tarde': return 'ops-arrival-late';
    case 'por_llegar': return 'ops-arrival-pending';
    case 'no_ha_llegado': return 'ops-arrival-missing';
    default: return 'ops-arrival-na';
  }
}

function ContactLink({ tg }: { tg: string }) {
  const handle = tg.replace(/^@/, '');
  if (/^[A-Za-z]/.test(handle)) {
    return (
      <a class="ops-contact" href={`https://t.me/${handle}`} target="_blank" rel="noreferrer">
        Contactar
      </a>
    );
  }
  return <span class="ops-contact">{tg}</span>;
}
